import { React, useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

import AppButton from "../../base/AppButton.component";
import VerificationLayout from "../../layout/VerificationLayout.component";
import SuccessGif from "../../../assets/gifs/success.json";
import { closeWindow, lottieLoopOptions } from "../../../utils/Utils";
import { Box } from "@mui/system";
import { useVerification } from "../../../context/Verification.context";
import {
  FETCH_INSURANCE_TRIGGER,
  LOG_KNOWLEDGEHUB_OPEN,
  TRANSACTION_HISTORY,
} from "../../../graphql/mutation/mutation";
import { useMutation } from "@apollo/client";
import moment from "moment";
import RenderArtifactContent from "./document/RenderArtifact.component";
import {
  withWalkthroughSlides,
  includesKnowledgeHubService,
  hasArtifacts,
} from "../../../utils/videoPlayer";

const VALID_KH_CHANNELS = ["email", "text", "device"];

const ThankYouComponent = ({
  skipped,
  setSkipped,
  artifactRefetch,
  artifactsLoading,
}) => {
  const verification = useVerification();
  const location = useLocation();

  const [close, setClose] = useState(false);
  const [saveResponse, setSaveResponse] = useState({});

  lottieLoopOptions.animationData = SuccessGif;
  lottieLoopOptions.loop = false;

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const filterData = {
    customerName: "",
    dealershipName: "",
    date: moment(
      new Date().toLocaleString("en-US", {
        timeZone: userTimezone,
      }),
    ).format("YYYY-MM-DD"),
    timestamp: new Date().getTimezoneOffset(),
  };

  const [fetchTransactions] = useMutation(TRANSACTION_HISTORY);

  const [fetchTrigger] = useMutation(FETCH_INSURANCE_TRIGGER, {
    onCompleted: (data) => {
      setSkipped(true);
      if (data) {
        fetchTransactions({
          variables: {
            transactionInput: {
              date: filterData.date,
              search_customer: filterData.customerName,
              search_dealership: filterData.dealershipName,
              timestamp: filterData.timestamp,
              pageCount: verification.fetchData.pageCount,
              rowsPerPage: 5,
              timezone: userTimezone,
            },
          },
        });
      }
    },
  });

  const baseArtifacts = useMemo(
    () =>
      Array.isArray(verification?.artifacts)
        ? verification?.artifacts
        : verification?.artifacts?.items || [],
    [verification?.artifacts],
  );

  const isRepull =
    verification?.isRepull === "true" || verification?.isRepull === true;

  const isContinueDelivery = !!verification?.isContinueDelivery;
  const isKhSession = includesKnowledgeHubService(verification);

  let playArtifactsAfterQuote = !!location?.state?.playArtifactsAfterQuote;
  if (!playArtifactsAfterQuote) {
    try {
      playArtifactsAfterQuote =
        sessionStorage.getItem("playArtifactsAfterQuote") === "true";
    } catch {
      playArtifactsAfterQuote = false;
    }
  }

  // KW when this session includes Knowledge Hub — force after quote so KW isn't skipped
  const includeWalkthroughRef = useRef(null);
  if (includeWalkthroughRef.current === null) {
    includeWalkthroughRef.current = {
      insurance: false,
      knowledgeHub: isKhSession,
      forceKnowledgeHub:
        isKhSession && (isContinueDelivery || playArtifactsAfterQuote),
    };
  }

  const artifactsWithWalkthrough = useMemo(
    () =>
      withWalkthroughSlides(baseArtifacts, verification, {
        includeInsurance: false,
        includeKnowledgeHub: includeWalkthroughRef.current.knowledgeHub,
        forceKnowledgeHub: includeWalkthroughRef.current.forceKnowledgeHub,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseArtifacts, verification?.walkthroughVideoUrl],
  );

  const handleArtifactsClose = (value) => {
    if (value) {
      verification.setIsContinueDelivery(false);
      verification.setIsArtifactOpen?.(true);
      try {
        sessionStorage.removeItem("playArtifactsAfterQuote");
      } catch {
        /* ignore */
      }
    }
    setClose(value);
  };

  // Artifacts for combined / KH sessions — never insurance-only.
  // playArtifactsAfterQuote is set by navigateAfterQuote (location state + sessionStorage).
  const showArtifacts =
    !close &&
    !isRepull &&
    (baseArtifacts.length > 0 || hasArtifacts(verification)) &&
    isKhSession &&
    (playArtifactsAfterQuote ||
      isContinueDelivery ||
      !verification?.isArtifactOpen);

  // Log Knowledge Hub open as soon as the KH starts playing. The channel
  // (email / text / device) comes from the Verify Insurance dialog link
  // (?via=) that Consent stored on the Verification context.
  const [logKnowledgeHubOpen] = useMutation(LOG_KNOWLEDGEHUB_OPEN);
  const hasLoggedKhRef = useRef(false);

  useEffect(() => {
    if (!showArtifacts || hasLoggedKhRef.current) return;

    const channel = verification?.khChannel;
    if (!VALID_KH_CHANNELS.includes(channel)) return; // backend rejects anything else
    if (!verification?.customer?.userId) return;

    hasLoggedKhRef.current = true;

    logKnowledgeHubOpen({
      variables: {
        input: {
          dealership_id: parseInt(verification.dealershipId),
          employee_user_id: parseInt(verification.employeeId),
          user_id: parseInt(verification.customer.userId),
          channel,
        },
      },
    })
      .then(() => verification.setKhChannel(null)) // never log twice, even after a refresh
      .catch((err) => console.error("Failed to log Knowledge Hub open:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArtifacts, verification?.khChannel, verification?.customer?.userId]);

  return (
    <>
      {showArtifacts ? (
        <RenderArtifactContent
          artifactsArray={artifactsWithWalkthrough}
          setClose={handleArtifactsClose}
          isThankyou={true}
          setSaveResponse={setSaveResponse}
          fetchTrigger={fetchTrigger}
          artifactRefetch={artifactRefetch}
          artifactsLoading={artifactsLoading}
        />
      ) : (
        <VerificationLayout>
          <Box
            component={"div"}
            height={{ xs: 200, sm: 200, md: 300, lg: 300, xl: 400 }}
            width={{ xs: 200, sm: 200, md: 300, lg: 300, xl: 400 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={verification?.dealershipLogo}
              width={150}
              height={60}
              alt="Dealership Logo"
              style={{ objectFit: "contain" }}
            />
          </Box>

          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              gap: 3,
            }}
          >
            <Typography variant="h6" align="center" fontWeight="bold">
              Thank You !!
            </Typography>
            <Typography variant="h6" align="center">
              Your insurance verification is completed and the proper parties
              will be notified
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppButton
              buttonText="FINISH"
              size="medium"
              variant="contained"
              sx={{
                width: "50%",
              }}
              clickHandler={() => {
                closeWindow();
              }}
            />
          </Box>
        </VerificationLayout>
      )}
    </>
  );
};
export default ThankYouComponent;